import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import EmailInput from '../EmailInput'
import SMSInput from '../SMSInput'
import MentionInput from '@SonarCX/components/molecules/Mention'

import {
  makePrivate,
  selectChatBox,
  togglePrivate,
  TYPES,
  updateValue
} from '@SonarCX/slices/chatBoxSlice'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useStyles from './styles'

const InputArea = ({ inputDisabled, checkSubmit }) => {
  const dispatch = useAppDispatch()

  const { values, currentID } = useAppSelector(selectChatBox)
  const { state: editState, update: updateEdit } = useChatEditState()
  const { conversation, id: session } = useQueueState()
  const { loadUser, allUsers } = useUserAPI()
  const { user: currentUser } = useEnvironment()

  const ExtraInput = React.useMemo(() => {
    switch (values.type) {
      case TYPES.EMAIL:
        return EmailInput
      case TYPES.SMS:
        return SMSInput
      case TYPES.LIVE:
      default:
        return () => null
    }
  }, [values.type])

  const placeholder = React.useMemo(() => {
    if (editState.editing) {
      return 'Edit last message...'
    }

    if (values.isPrivate) {
      return 'Add a note...'
    }

    return 'Send a message...'
  }, [editState.editing, values.isPrivate])

  const value = React.useMemo(() => {
    if (editState.editing) {
      return editState.newMessage
    }

    const trimmed = (values.message ?? '').trim()
    if (trimmed.length === 0) {
      return ''
    }

    return values.message
  }, [editState.editing, editState.newMessage, values.message])

  const userIDs = React.useMemo(() => conversation?.users ?? [], [conversation])

  const classes = useStyles({ isPrivate: values.isPrivate })

  // Loads users and stores channels
  React.useEffect(() => {
    if (!currentID) {
      return
    }

    const users = {}

    // Load users
    userIDs.forEach(id => {
      if (id !== currentUser?.id) {
        const user = allUsers[id]
        if (!user) {
          loadUser(id)
        } else {
          users[id] = user
        }
      }
    })

    const allEmails = {}
    const allPhones = {}

    // Find channels for loaded users
    Object.values(users).forEach(user => {
      user.channels.forEach(channel => {
        const toStore = { user, channel }

        if (channel.type === 'email') {
          allEmails[channel.id] = toStore
        } else if (channel.type === 'sms_voice') {
          allPhones[channel.id] = toStore
        }
      })
    })

    dispatch(updateValue('allEmails', allEmails))
    dispatch(updateValue('allPhones', allPhones))
  }, [currentID, userIDs, allUsers, loadUser, dispatch, currentUser])

  const handleChange = React.useCallback(
    e => {
      const val = e.target.value
      if (editState.editing) {
        updateEdit(val)
      } else {
        dispatch(updateValue('message', val))
      }
    },
    [editState.editing, updateEdit, dispatch]
  )

  const handleOnMentionAdd = React.useCallback(() => {
    dispatch(makePrivate())
  }, [dispatch])

  const handleOnMentionCancel = React.useCallback(() => {
    dispatch(togglePrivate())
  }, [dispatch])

  return (
    <div className={classes.root}>
      <ExtraInput disabled={inputDisabled} />

      <MentionInput
        value={value}
        placeholder={placeholder}
        onKeyDown={checkSubmit}
        onChange={handleChange}
        users={userIDs}
        focus={session}
        className={classes.mentionInputClass}
        inputClass='text'
        type={values.type}
        disabled={inputDisabled}
        onMentionAdd={handleOnMentionAdd}
        onMentionCancel={handleOnMentionCancel}
      />
    </div>
  )
}

export default InputArea
